import {useHasMounted} from "./useHasMounted";

/**
 * The origin this page is being served from, or `""` until the component has mounted.
 *
 * The sibling of {@link ./useMountedHostname}, and a thin adapter for the same reason (epic #26):
 * the rule that turns an origin into a URL somebody can click lives in `nav/displayedOrigin.ts` and
 * is unit-tested without React. This supplies the one input it cannot take for itself, and the
 * hydration gate that makes taking it safe.
 *
 * Origin rather than hostname because these values become real links: a development host is
 * `localhost:3000`, and a link that drops the port opens nothing.
 *
 * **The gate is required, not defensive.** One pre-rendered HTML tree is served to several hosts, so
 * the static markup carries whatever the bake could compute with no host at all. A first client
 * render that read `window.location.origin` would differ from the served HTML on exactly the hosts
 * where the answer matters, and React would discard the mismatched subtree (ui#74/#418).
 *
 * `""` — rather than `null` — because it is the honest input to the rules downstream: they answer
 * "no host signal" for it without a caller branching first, so the pre-mount pass runs the SAME rule
 * the post-mount pass does instead of a hand-written stand-in that can drift from it.
 */
export function useMountedOrigin(): string {
	return useHasMounted() ? window.location.origin : "";
}
